/**
 * Лента заметок плана (DEV-1658).
 *
 * Стор, а не локальное состояние компонента: ленту показывают и колонкой справа
 * от стека (макет 9-5), и на весь экран в мит-режиме (макет 9-7). Это один и тот
 * же список, и переключение режима не должно перечитывать его с сервера — иначе
 * заметка, написанная секунду назад, на мгновение пропадала бы с экрана.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { createNote, deleteNote, listNotes, updateNote, type Note } from './notes'

export const useNotesStore = defineStore('plan-notes', () => {
  const items = ref<Note[]>([])
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<unknown>(null)

  /**
   * Заметка, которую сейчас разбирает ассистент.
   *
   * Признак живёт здесь, а не в компоненте: разбор идёт в панели ассистента, а
   * она переживает и уход с экрана, и смену режима. Без него применённый
   * черновик не к чему было бы привязать — панель знает только про созданные
   * задачи, но не про то, из какой мысли они выросли.
   */
  const parsingId = ref('')

  /**
   * Встреча, чью ленту показываем. Пусто — обычная лента: свои личные заметки
   * плюс заметки всех встреч, где человек был (DEV-1947).
   */
  const sessionId = ref('')

  const fresh = computed(() => items.value.filter((note) => !note.processedAt))

  const load = async () => {
    isLoading.value = true

    try {
      items.value = await listNotes(sessionId.value)
      isLoaded.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Записать заметку. Список обновляется ответом сервера, а не своей копией:
   * идентификатор и время создания ставит он, а без идентификатора карточку
   * нечем ни разобрать, ни удалить.
   */
  const add = async (body: string) => {
    const text = body.trim()

    if (!text) return

    try {
      const note = await createNote(text, sessionId.value)

      if (note) items.value = [note, ...items.value]
    } catch (failure) {
      error.value = failure
    }
  }

  const replace = (note: Note) => {
    items.value = items.value.map((item) => (item.id === note.id ? note : item))
  }

  /** Заметка разобрана: ключи вышедших из неё задач остаются в ней навсегда */
  const markProcessed = async (id: string, issueKeys: string[]) => {
    try {
      const note = await updateNote(id, { processed: true, issueKeys })

      if (note) replace(note)
    } catch (failure) {
      error.value = failure
    } finally {
      if (parsingId.value === id) parsingId.value = ''
    }
  }

  const remove = async (id: string) => {
    const saved = items.value

    items.value = items.value.filter((item) => item.id !== id)

    try {
      await deleteNote(id)
    } catch (failure) {
      // На встрече заметку мог снести сосед секундой раньше: «её нет» — это
      // ровно то, чего мы добивались, и возвращать её на экран незачем.
      if (isGone(failure)) return

      items.value = saved
      error.value = failure
    }
  }

  /** Правка заметки: на встрече текст поправляют прямо в карточке */
  const edit = async (id: string, body: string) => {
    const text = body.trim()

    if (!text) return

    try {
      const note = await updateNote(id, { body: text })

      if (note) replace(note)
    } catch (failure) {
      error.value = failure
    }
  }

  /** Показать ленту одной встречи или вернуться к обычной */
  const setSession = async (id: string) => {
    if (sessionId.value === id) return

    sessionId.value = id
    await load()
  }

  const startParsing = (id: string) => {
    parsingId.value = id
  }

  return {
    items,
    fresh,
    sessionId,
    isLoading,
    isLoaded,
    error,
    parsingId,
    load,
    add,
    markProcessed,
    remove,
    edit,
    setSession,
    startParsing,
  }
})

/** Ответ «такой заметки нет»: её уже удалили, и это не ошибка нашего экрана */
const isGone = (failure: unknown): boolean =>
  (failure as { response?: { status?: number } })?.response?.status === 404
